import mBaseModel from '@/models/mBaseModel';
import UtilsData from '@/utilities/UtilsData';
import mDealStacrLiability from '@/models/mDealStacrLiability';
import formatter from '@/utilities/Formatter';
import moment from 'moment';
import UtilsObject from '@/utilities/UtilsObject';
import mDealStacrCollateralInformation from '@/models/mDealStacrCollateralInformation';

import { ModelWithSmartProperties } from '@/models/ModelWithSmartProperties';
import { ModelFormatters } from '@/models/ModelFormatters';
import { ModelPropertyInfos } from '@/models/ModelPropertyInfos';

export default class mDealStacr extends ModelWithSmartProperties {
  static DefaultCollateralInformation() {
    return [
      {},
    ]
  }

  static DefaultLiability() {
    return [
      { BondName: 'A', Detachment: 100.0, Thickness: NaN, Balance: NaN, Coupon: 0.0, Spread: 0.0, },
      { BondName: 'M-1', Detachment: 2.5, Thickness: NaN, Balance: NaN, Coupon: 0.0, Spread: 0.0, },
    ]
  }

  static Defaults = () => {
    const now = new Date();
    return {
      Name: 'STACR 2022-AA',
      CollateralInformation: mDealStacr.DefaultCollateralInformation(),
      Liability: mDealStacr.DefaultLiability(),
      ContractInformation: {
        ClosingDate: parseInt(moment()
          .format('YYYYMMDD'), 10),
        FirstPayDate: parseInt(moment()
          .add(1, 'day')
          .month(now.getDate() >= 25 ? now.getMonth() + 1 : now.getMonth())
          .date(25)
          .format('YYYYMMDD'), 10),
        EarlyRedemptionDate: parseInt(moment()
          .add(2, 'month')
          .add(2, 'day')
          .format('YYYYMMDD'), 10),
        MaturityDate: parseInt(moment()
          .add(3, 'month')
          .add(3, 'day')
          .format('YYYYMMDD'), 10),
        CancellationPct: 10,
      },
      Structure: {
        CETarget: 1.0,
        DlqTarget: 50,
        DlqMonthsAverage: 6,
        UseCumLossTest: true,
      },
    };
  };

  static vocDbToLabel(dbName = null) {
    const ats = Object.freeze({
      Name: 'STACR Name',
      CollateralInformation: {
        ReferencePoolBalance: 'Reference Pool Balance',
        Wac: 'WAC',
        Wam: 'WAM',
        Wala: 'WALA',
      },
      ContractInformation: {
        _ownName: 'Contract Information',
        ClosingDate: 'Closing Date',
        FirstPayDate: 'First Pay Date',
        MaturityDate: 'Maturity Date',
        EarlyRedemptionDate: 'Early Redemption Date',
        CancellationPct: 'Cancellation Percent',
      },
      Structure: {
        _ownName: 'STACR Structure',
        CETarget: 'CE Target',
        DlqTarget: 'Delinquency Target',
        DlqMonthsAverage: 'Dlq Months Average',
        UseCumLossTest: 'Use Cum Loss Test',
      },
      Liability: 'STACR Liability Description'
    });
    if (dbName) {
      return UtilsObject.getByPath(ats, dbName);
    }
    return ats;
  }

  constructor(rawModelFields = {}) {
    super(rawModelFields);
    this.ats = {
      ...mDealStacr.Defaults(),
    };

    const datePropertyInfo = {
      ToUserDataFormatter: ModelFormatters.ToUserData.NumberAsDate(),
      // ToModelDataFormatter: ModelFormatters.ToModelData.DateAsNumber()
    };

    this.defineModelProperty('Name', ModelPropertyInfos.Text);

    this.defineModelProperty('ContractInformation.ClosingDate', datePropertyInfo);
    this.defineModelProperty('ContractInformation.FirstPayDate', datePropertyInfo);
    this.defineModelProperty('ContractInformation.MaturityDate', datePropertyInfo);
    this.defineModelProperty('ContractInformation.EarlyRedemptionDate', datePropertyInfo);
    this.defineModelProperty('ContractInformation.CancellationPct', ModelPropertyInfos.DecimalNumber);

    this.defineModelProperty('Structure.CETarget', ModelPropertyInfos.DecimalNumber);
    this.defineModelProperty('Structure.DlqTarget', ModelPropertyInfos.DecimalNumber);
    this.defineModelProperty('Structure.DlqMonthsAverage', ModelPropertyInfos.IntegerNumber);
    this.defineModelProperty('Structure.UseCumLossTest', {
      ToUserDataFormatter: ModelFormatters.ToUserData.Passthrough(),
      ToModelDataFormatter: ModelFormatters.ToModelData.Passthrough()
    });

    this.setRawFields({
      ...this.ats,
      ...rawModelFields
    });
  }

  setRawFields(rawModelFields = {}) {
    const {
      CollateralInformation: collateralInformation = [],
      Liability: liability = []
    } = rawModelFields;
    /*
        for (let idx = 0; idx < liability.length; idx++) {
          if (idx === 0) {
            liability[idx].Thickness = 100;
            liability[idx].Balance = balancesSum;
            continue;
          }
    
          const { Detachment: prevDetachment = 0.0 } = liability[idx - 1];
          const { Detachment: currDetachment = 0.0 } = liability[idx];
    
          liability[idx].Thickness = 0.0;
          liability[idx].Balance = 0.0;
    
          liability[idx - 1].Thickness = prevDetachment - currDetachment;
          liability[idx - 1].Balance = balancesSum * (liability[idx - 1].Thickness / 100);
        }
    */
    this.ats.CollateralInformation = collateralInformation.map(el => new mDealStacrCollateralInformation(el));
    this.ats.Liability = liability.map(el => new mDealStacrLiability(el));

    this.calculateAutoFields();

    for (let propName of Object.keys(rawModelFields)) {
      if (!this.ats.hasOwnProperty(propName)) {
        continue;
      }
      switch (propName) {
        case 'Name':
          ModelWithSmartProperties.setPropertyByPath(this, `${propName}$`, rawModelFields[propName]);
          break;
        case 'Structure':
        case 'ContractInformation':
          for (let [childPropName, childPropValue] of Object.entries(rawModelFields[propName])) {
            if (childPropName in this.ats[propName]) {
              this.ats[propName][childPropName] = undefined;
              ModelWithSmartProperties.setPropertyByPath(this, `${propName}.${childPropName}$`, childPropValue);
            }
          }
          break;
      }
    }
    return this;
  }

  calculateAutoFields() {
    const {
      CollateralInformation: collateralInformation,
      Liability: liability
    } = this.ats;

    if (collateralInformation == null || liability == null) {
      return;
    }

    let balancesSum = 0.0;
    for (let idx = 0; idx < collateralInformation.length; idx++) {
      balancesSum += collateralInformation[idx].ReferencePoolBalance$;
    }

    for (let idx = 0; idx < liability.length; idx++) {
      if (idx === 0) {
        liability[idx].Thickness$ = 100;
        liability[idx].Balance$ = balancesSum;
        continue;
      }

      const prevDetachment = liability[idx - 1].Detachment$;
      const currDetachment = liability[idx].Detachment$;
      const thicknessDiff = prevDetachment - currDetachment;

      liability[idx - 1].Thickness$ = thicknessDiff;
      liability[idx - 1].Balance$ = balancesSum * (thicknessDiff / 100);

      liability[idx].Thickness$ = currDetachment;
      liability[idx].Balance$ = balancesSum * (currDetachment / 100);
    }
  }

  getRawFieldsFormattedForBackEnd() {
    const res = {};
    res.Name = this.Name$;

    res.ContractInformation = {
      ClosingDate: ModelFormatters.ToModelData.DateAsNumber()(null, this.ContractInformation.ClosingDate$),
      FirstPayDate: ModelFormatters.ToModelData.DateAsNumber()(null, this.ContractInformation.FirstPayDate$),
      MaturityDate: ModelFormatters.ToModelData.DateAsNumber()(null, this.ContractInformation.MaturityDate$),
      EarlyRedemptionDate: ModelFormatters.ToModelData.DateAsNumber()(null, this.ContractInformation.EarlyRedemptionDate$),
      CancellationPct: this.ContractInformation.CancellationPct$,
    };

    res.Structure = {
      CETarget: this.Structure.CETarget$,
      DlqTarget: this.Structure.DlqTarget$,
      DlqMonthsAverage: this.Structure.DlqMonthsAverage$,
      UseCumLossTest: this.Structure.UseCumLossTest$
    };

    res.Liability = this.ats.Liability.map(item => item.getRawFieldsFormattedForBackEnd());
    res.CollateralInformation = this.ats.CollateralInformation.map(item => item.getRawFieldsFormattedForBackEnd());

    return res;
  }

  validate() {
    const createErrorDescription = (msg, fieldName, fieldSysName) => {
      return {
        modelName: 'STACR',
        field: fieldName,
        msg,
        fieldSysName
      };
    };
    const flattenTopLevelProperties = (obj, prefix) => Object.entries(obj[prefix])
      .map(([_propName, _propValue]) => [`${prefix}.${_propName}`, _propValue])
    const flattenObjectProperties = (obj, prefix) => Object.entries(obj[prefix])
      .map(([_propName, _propValue]) => [`${prefix}.${_propName}`, _propValue]);
    const flattenArrayProperties = (array, prefix) => array.map(
      (_value, _idx) => [`${prefix}.${_idx}`, _value]);

    const ats = this.getRawFieldsFormattedForBackEnd();

    this._localValidationStatus = true;
    this._localValidationResults = [];

    // Validate collateral information and liability models and grab validation errors with the keys adjustments
    for (let [key, value] of [
      ...flattenArrayProperties(this.ats.CollateralInformation, 'CollateralInformation'),
      ...flattenArrayProperties(this.ats.Liability, 'Liability'),
    ]) {
      const validationStatus = value.validate();
      validationStatus.forEach(validationError => {
        this._localValidationResults.push({
          ...validationError,
          fieldSysName: `${key}.${validationError.fieldSysName}`
        });
      });
    }

    // 'CE Target' is a percentage, where 0 < Target < 25
    // and defines the Deal's minimum required Credit Enhancement… NO default.
    if (!(this.Structure.CETarget$ > 0 && this.Structure.CETarget$ < 25)) {
      this._localValidationResults.push(createErrorDescription(
        '0 < CETarget < 25',
        mDealStacr.vocDbToLabel('Structure.CETarget'),
        'Structure.CETarget'
      ));
    }

    // 'Delinquency Target' is a percentage, where 0 < Target < 100…
    // and defines the Deal's maximum allowable Delinquency level (default is 50).
    if (!(this.Structure.DlqTarget$ > 0 && this.Structure.DlqTarget$ < 100)) {
      this._localValidationResults.push(createErrorDescription(
        '0 < DlqTarget < 100',
        mDealStacr.vocDbToLabel('Structure.DlqTarget'),
        'Structure.DlqTarget'
      ));
    }

    // 'Dlq Months Average' is an integer, where 0 < Months < 12…
    // and defines the time frame over which Delinquencies are averaged (default is 6).
    if (!(this.Structure.DlqMonthsAverage$ > 0 && this.Structure.DlqMonthsAverage$ < 12)) {
      this._localValidationResults.push(createErrorDescription(
        '0 < DlqMonthsAverage < 12',
        mDealStacr.vocDbToLabel('Structure.DlqMonthsAverage'),
        'Structure.DlqMonthsAverage'
      ));
    }

    const closingDate = this.ContractInformation.ClosingDate$;
    const firstPayDate = this.ContractInformation.FirstPayDate$;
    const maturityDate = this.ContractInformation.MaturityDate$;
    const earlyRedemptionDate = this.ContractInformation.EarlyRedemptionDate$;

    // 'Maturity Date' is a date, where 'First Payment Date' <  'Maturity Date'
    // and defines the date on which the ACIS Contract matures… NO default.

    // 'Closing Date' is a date, where 1/1/2010 < Date
    // and defines the date on which the STACR Deal is issued… NO default.																

    // 'First Payment Date' is a date, where 'Closing Date'  < Date < 'Maturity Date'… and defines the date on which the Deal owner receives their first cash flow; 																
    // there is NO default, BUT the month must be the same MONTH (or within 2 months of) the 'Closing Date' and the DAY must be the 25th.																
    // For example, if Closing is 10/1/2021, the first payment date may ONLY be 1/25/2021, 2/25/2021, or 3/25/2021.																
    if (!(closingDate.getTime() < firstPayDate.getTime() && firstPayDate.getTime() < maturityDate.getTime())) {
      this._localValidationResults.push(createErrorDescription(
        'Closing < First Payment < Maturity',
        mDealStacr.vocDbToLabel('ContractInformation.FirstPayDate'),
        'ContractInformation.FirstPayDate'));
    }

    // 'Early Redemption Date' is a date,
    // where 'First Payment Date' < Date <  'Maturity Date'…
    // and defines the date on which the ACIS Contract may be cancelled… NO default.																
    if (!(firstPayDate.getTime() < earlyRedemptionDate.getTime() && earlyRedemptionDate.getTime() < maturityDate.getTime())) {
      this._localValidationResults.push(createErrorDescription(
        'First Payment < Early Redemption <  Maturity',
        mDealStacr.vocDbToLabel('ContractInformation.EarlyRedemptionDate'),
        'ContractInformation.EarlyRedemptionDate'));
    }

    // 'Cancellation Percent' is a percentage, where 0 <= Percent <  25
    // and defines the percentage of the original Pool Balance at which ACIS Contract may be cancelled (default is 10%).																
    if (!(this.ContractInformation.CancellationPct$ >= 0 && this.ContractInformation.CancellationPct$ < 25)) {
      this._localValidationResults.push(createErrorDescription(
        '0 <= Cancellation < 25',
        mDealStacr.vocDbToLabel('ContractInformation.CancellationPct'),
        'ContractInformation.CancellationPct'
      ));
    }

    const entriesToValidate = [
      ...flattenTopLevelProperties(ats, 'ContractInformation'),
      ...flattenTopLevelProperties(ats, 'Structure'),
      ...Object.entries(ats)
        .filter(([_name, _value]) => (!/CollateralInformation|ContractInformation|Structure|Liability/.test(_name))),
    ];

    for (let [fieldSysName, fValue] of entriesToValidate) {
      const fieldName = mDealStacr.vocDbToLabel(fieldSysName)
      const error = createErrorDescription('', fieldName, fieldSysName);
      switch (fieldSysName) {
        case 'Name':
          error.msg = 'Name mask is: STACR YYYY-[a-z0-9]{3}*';
          if (!/STACR(\s[a-zA-Z-]+)*\s\d{4}-[0-9a-zA-Z]{3}[0-9a-zA-Z-]*$/gm.test(fValue)) {
            this._localValidationResults.push(error);
          }
          break;
        default:
          error.msg = 'Field is required.';
          if (typeof fValue === 'number') {
            if (isNaN(fValue) || fValue == null) {
              this._localValidationResults.push(error);
            }
          } else {
            if (UtilsData.isEmpty(fValue)) {
              this._localValidationResults.push(error);
            } else if (fValue === 'Invalid date') {
              error.msg = 'Invalid Date.';
              this._localValidationResults.push(error);
            }
          }
      }
    }
    this._localValidationStatus = this._localValidationResults.length === 0;
    return this._localValidationStatus;
  }
}
